import NightsStayIcon from "@mui/icons-material/NightsStay";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import { Chip, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";

import type { SlotKind } from "@/api/types";

type StyleSpec = {
  color: "default" | "error" | "warning" | "info" | "success" | "primary" | "secondary";
  icon?: React.ReactElement;
};

const STYLE: Record<SlotKind, StyleSpec> = {
  GARDE: { color: "error", icon: <NightsStayIcon /> },
  DEMI_GARDE: { color: "warning", icon: <WbSunnyIcon /> },
  POSTE: { color: "default" },
  ASTREINTE: { color: "info", icon: <NotificationsActiveIcon /> },
};

/**
 * Compact chip indicating the slot kind. ``POSTE`` is the default daytime
 * shift and renders nothing when ``hideDefault`` is true — keeps the planning
 * grid uncluttered.
 */
export function SlotKindChip({
  kind,
  hideDefault = true,
  size = "small",
}: {
  kind: SlotKind;
  hideDefault?: boolean;
  size?: "small" | "medium";
}) {
  const { t } = useTranslation();
  if (hideDefault && kind === "POSTE") return null;
  const style = STYLE[kind];
  const label = t(`slotKind.${kind}`);
  const glossKey =
    kind === "GARDE" ? "garde"
    : kind === "DEMI_GARDE" ? "demi_garde"
    : kind === "ASTREINTE" ? "astreinte"
    : "poste";
  const definition = t(`glossary.${glossKey}.def`, { defaultValue: "" });
  const tip = definition ? `${label} — ${definition}` : label;
  return (
    <Tooltip title={tip}>
      <Chip
        size={size}
        color={style.color}
        icon={style.icon}
        label={label}
        sx={{
          height: size === "small" ? 18 : 22,
          fontSize: size === "small" ? 10 : 11,
          fontWeight: 700,
          "& .MuiChip-icon": {
            fontSize: size === "small" ? 12 : 14,
            ml: "4px",
            mr: "-2px",
          },
          "& .MuiChip-label": { px: 0.75 },
        }}
      />
    </Tooltip>
  );
}
